import { useEffect, useState } from "react";
import { Gavel, Sparkles, Loader2, FileText, Download } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import MicButton from "@/components/ui/MicButton";
import SafeBlock from "@/components/ui/SafeBlock";
import ReportShell from "@/components/reports/ReportShell";
import ReportVersionsBar from "@/components/reports/ReportVersionsBar";
import PrevisionnelSelect from "@/components/memo/PrevisionnelSelect";
import { startRdvAjJob, fetchRdvAjJob, fetchLastRdvAj, downloadRdvAjPdf } from "@/lib/rdvaj";

const KIND = "premier_rdv_aj";

export default function PremierRdvAjSection({ dossierId }) {
    const [titre, setTitre] = useState("1 - Premier RDV AJ");
    const [dateRdv, setDateRdv] = useState("");
    const [prevId, setPrevId] = useState("");
    const [briefing, setBriefing] = useState("");
    const [content, setContent] = useState("");
    const [sessionId, setSessionId] = useState(null);
    const [generatedAt, setGeneratedAt] = useState(null);
    const [generating, setGenerating] = useState(false);
    const [stage, setStage] = useState("");
    const [exporting, setExporting] = useState(false);
    const [refreshKey, setRefreshKey] = useState(0);

    useEffect(() => {
        fetchLastRdvAj(dossierId).then((r) => {
            if (r) { setContent(r.content || ""); setSessionId(r.session_id); setGeneratedAt(r.created_at); if (r.titre) setTitre(r.titre); if (r.date_rdv) setDateRdv(r.date_rdv); if (r.briefing) setBriefing(r.briefing); }
        }).catch(() => {});
    }, [dossierId]);

    function loadVersion(d) {
        if (!d) { setContent(""); setSessionId(null); setGeneratedAt(null); return; }
        setContent(d.content || ""); setSessionId(d.session_id); setGeneratedAt(d.created_at);
        if (d.titre) setTitre(d.titre); if (d.date_rdv) setDateRdv(d.date_rdv); if (d.briefing) setBriefing(d.briefing);
    }

    async function generate() {
        if (generating) return;
        setGenerating(true); setContent(""); setSessionId(null); setStage("preparation");
        try {
            const { job_id } = await startRdvAjJob(dossierId, { titre: titre.trim() || "1 - Premier RDV AJ", date_rdv: dateRdv || null, briefing: briefing || null, previsionnel_id: prevId || null });
            for (;;) {
                await new Promise((r) => setTimeout(r, 3000));
                const j = await fetchRdvAjJob(dossierId, job_id);
                setStage(j.stage);
                if (j.stage === "done") { setContent(j.content || ""); setSessionId(j.session_id); setGeneratedAt(new Date().toISOString()); setRefreshKey((k) => k + 1); toast.success("Rapport « Premier RDV AJ » généré"); break; }
                if (j.stage === "error") { toast.error("Erreur IA", { description: String(j.error), duration: 12000 }); break; }
            }
        } catch (e) { toast.error("Génération impossible", { description: e.message }); }
        finally { setGenerating(false); setStage(""); }
    }

    async function exportPdf() {
        setExporting(true);
        try { await downloadRdvAjPdf(dossierId, sessionId); toast.success("PDF téléchargé"); }
        catch (e) { toast.error("Export PDF impossible", { description: e.message }); }
        finally { setExporting(false); }
    }

    return (
        <section data-testid="premier-rdv-aj-section" className="space-y-6">
            <div className="flex flex-wrap items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                    <div className="h-9 w-9 rounded-full bg-primary flex items-center justify-center"><Gavel className="h-4 w-4 text-primary-foreground" strokeWidth={1.75} /></div>
                    <div>
                        <h3 className="font-display text-xl text-foreground">1 - Premier RDV AJ</h3>
                        <p className="text-xs text-muted-foreground">Préparation du premier rendez-vous avec l&apos;Administrateur Judiciaire · croise Dataroom, Data Warehouse JSON et Questionnaire.</p>
                    </div>
                </div>
                <Button type="button" data-testid="rdvaj-generate" onClick={generate} disabled={generating} className="rounded-full bg-primary hover:bg-primary/90 text-primary-foreground">
                    {generating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} {generating ? (stage === "generation" ? "Rédaction du rapport…" : "Lecture du dossier…") : "Générer le rapport"}
                </Button>
            </div>

            <Card className="border-slate-200 bg-white shadow-sm"><CardContent className="pt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                    <label className="text-sm text-muted-foreground" htmlFor="rdvaj-titre">Titre du rapport</label>
                    <Input id="rdvaj-titre" data-testid="rdvaj-titre" value={titre} onChange={(e) => setTitre(e.target.value)} disabled={generating} className="h-10 bg-background" placeholder="1 - Premier RDV AJ" />
                </div>
                <div className="space-y-1.5">
                    <label className="text-sm text-muted-foreground" htmlFor="rdvaj-date">Date du RDV</label>
                    <Input id="rdvaj-date" type="date" data-testid="rdvaj-date-rdv" value={dateRdv} onChange={(e) => setDateRdv(e.target.value)} disabled={generating} className="h-10 bg-background" />
                </div>
                <div className="space-y-1.5 md:col-span-2">
                    <PrevisionnelSelect dossierId={dossierId} value={prevId} onChange={setPrevId} disabled={generating} testId="rdvaj-previsionnel-ref" />
                </div>
                <div className="space-y-1.5 md:col-span-2">
                    <label className="text-sm text-muted-foreground" htmlFor="rdvaj-briefing">Briefing (consignes à intégrer)</label>
                    <div className="flex items-start gap-2">
                        <Textarea id="rdvaj-briefing" data-testid="rdvaj-briefing" value={briefing} onChange={(e) => setBriefing(e.target.value)} disabled={generating} placeholder="Contexte, points à mettre en avant devant l'AJ, créanciers sensibles…" className="bg-background resize-y text-sm min-h-[240px]" />
                        <MicButton testId="rdvaj-briefing-mic" disabled={generating} onText={(t) => setBriefing((v) => (v ? v + " " : "") + t)} />
                    </div>
                </div>
            </CardContent></Card>

            {(content || generating) && (
                <Card data-testid="rdvaj-result" className="border-slate-200 bg-white shadow-sm"><CardContent className="pt-6 space-y-3">
                    <div className="flex items-center gap-2">
                        <FileText className="h-4 w-4 text-primary" /><h3 className="font-display text-lg text-foreground">{titre || "1 - Premier RDV AJ"}</h3>
                        <ReportVersionsBar dossierId={dossierId} kind={KIND} currentSessionId={sessionId} onLoad={loadVersion} refreshKey={refreshKey} disabled={generating} />
                    </div>
                    <SafeBlock label="Le rapport"><ReportShell dossierId={dossierId} sessionId={sessionId} kind={KIND} content={content} onContentChange={setContent} charts={[]} onChartsChange={() => {}} streaming={generating} /></SafeBlock>
                    {!content && <p className="text-sm text-muted-foreground flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Génération en cours…</p>}
                    {content && !generating && (
                        <div className="flex justify-end border-t border-slate-200 pt-4">
                            <Button type="button" variant="outline" data-testid="rdvaj-export-pdf" onClick={exportPdf} disabled={exporting} className="rounded-full">{exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} Télécharger PDF</Button>
                        </div>
                    )}
                </CardContent></Card>
            )}
        </section>
    );
}
